import { Link } from 'react-router-dom';
import { Heart } from 'lucide-react';
import { formatPrice, calculateDiscount } from '../../lib/utils';
import { useWishlist } from '../../context/WishlistContext';
import { useAuth } from '../../context/AuthContext';
import { cn } from '../../lib/utils';
import { resolveImageUrl } from '../../lib/imageUrl';
import { effectiveUnitPrice, hasActiveDiscount } from '../../utils/pricing';

export const ProductCard = ({ product, onQuickAdd }) => {
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist();
  const { user, login } = useAuth();
  
  const isWishlisted = isInWishlist(product.product_id);
  const effective = effectiveUnitPrice(product, null);
  const hasDiscount = hasActiveDiscount(product, null);
  const discount = hasDiscount ? calculateDiscount(product.price, product.sale_price) : 0;
  const totalStock = product.variants?.reduce((sum, v) => sum + (v.stock || 0), 0) || 0;
  const isLowStock = totalStock > 0 && totalStock <= 10;

  const handleWishlistClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      login();
      return;
    }
    
    if (isWishlisted) {
      await removeFromWishlist(product.product_id);
    } else {
      await addToWishlist(product.product_id);
    }
  };

  return (
    <div className="product-card group" data-testid={`product-card-${product.product_id}`}>
      <Link to={`/product/${product.product_id}`}>
        {/* Image Container */}
        <div className="product-image-container relative">
          {product.images?.[0]?.url ? (
            <img 
              src={resolveImageUrl(product.images[0].url)} 
              alt={product.name}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full bg-[#F5F4F2] flex items-center justify-center">
              <span className="text-[#E0DFDD] font-serif text-2xl">Bloom</span>
            </div>
          )}

          {/* Badges */}
          <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5 items-start">
            {hasDiscount && (
              <span className="sale-badge !static" data-testid="sale-badge">
                -{discount}%
              </span>
            )}
            {product.is_nouvel_arrivage && (
              <span className="new-badge !static" data-testid="new-badge">
                Nouvel Arrivage
              </span>
            )}
            {product.is_bestseller && (
              <span
                className="bg-[#1A1514] text-[#D4AF37] border border-[#D4AF37] text-[10px] font-bold px-2 py-1 uppercase tracking-wider shadow-sm flex items-center gap-1"
                data-testid="top-seller-badge"
              >
                <span aria-hidden="true">★</span> Top Vente
              </span>
            )}
            {isLowStock && !hasDiscount && !product.is_nouvel_arrivage && !product.is_bestseller && (
              <span className="bg-red-600 text-white text-[10px] font-bold px-2 py-1 uppercase tracking-wider" data-testid="low-stock-badge">
                Derniers articles
              </span>
            )}
          </div>

          {/* Wishlist Button */}
          <button
            onClick={handleWishlistClick}
            className={cn(
              "wishlist-btn",
              isWishlisted && "opacity-100 transform-none"
            )}
            data-testid={`wishlist-btn-${product.product_id}`}
          >
            <Heart 
              size={16} 
              className={cn(
                "transition-colors",
                isWishlisted ? "fill-[#C87979] text-[#C87979]" : "text-[#1A1514]"
              )} 
            />
          </button>

          {/* Quick Add (optional) */}
          {onQuickAdd && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onQuickAdd(product);
              }}
              className="quick-add-btn"
              data-testid={`quick-add-${product.product_id}`}
            >
              Aperçu Rapide
            </button>
          )}
        </div>

        {/* Product Info */}
        <div className="mt-4">
          <h3 className="font-serif text-lg text-[#1A1514] group-hover:text-[#D4AF37] transition-colors">
            {product.name}
          </h3>
          
          {/* Rating */}
          {product.review_count > 0 && (
            <div className="flex items-center gap-2 mt-1">
              <div className="star-rating">
                {[...Array(5)].map((_, i) => (
                  <span 
                    key={i} 
                    className={i < Math.round(product.average_rating) ? 'star-filled' : 'star-empty'}
                  >
                    ★
                  </span>
                ))}
              </div>
              <span className="text-xs text-[#6B6A68]">({product.review_count})</span>
            </div>
          )}

          {/* Price */}
          <div className="flex items-center gap-2 mt-2">
            {hasDiscount ? (
              <>
                <span className="price-sale">{formatPrice(effective)}</span>
                <span className="price-original">{formatPrice(product.price)}</span>
              </>
            ) : (
              <span className="text-[#1A1514] font-medium">{formatPrice(effective)}</span>
            )}
          </div>

          {/* Colors (if multiple) */}
          {product.colors && product.colors.length > 1 && (
            <p className="text-xs text-[#6B6A68] mt-2">
              {product.colors.length} couleurs disponibles
            </p>
          )}
        </div>
      </Link>
    </div>
  );
};
